import { useRef, useState } from "react";
import { Upload, Loader2, ImageOff } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/**
 * Drag-and-drop / click-to-upload image picker for the admin editor.
 *
 * Props:
 *   onUploaded({ url, thumbnail_url, original_url, filename, width, height }) — required
 *   label  – button text (default "Upload Image")
 *   testid – data-testid prefix
 *   compact – render as a small button instead of a full drop-zone
 */
export default function ImageUploader({ onUploaded, label = "Upload Image", testid = "image-uploader", compact = false }) {
  const [busy, setBusy] = useState(false);
  const [hover, setHover] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) {
      toast.error("Only JPG, PNG, or WEBP images are accepted.");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error("Image is larger than 20MB.");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    setProgress(0);
    try {
      const res = await api.post("/upload/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      toast.success(`Uploaded ${res.data.filename}`);
      onUploaded?.(res.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Upload failed";
      toast.error(msg);
    } finally {
      setBusy(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setHover(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) upload(file);
  };

  const onPick = (e) => upload(e.target.files?.[0]);

  if (compact) {
    return (
      <>
        <button
          type="button"
          className="gold-btn"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={`${testid}-btn`}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {busy ? `Uploading ${progress}%` : label}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={onPick}
          data-testid={`${testid}-input`}
        />
      </>
    );
  }

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setHover(true); }}
      onDragLeave={() => setHover(false)}
      onDrop={onDrop}
      onClick={() => !busy && inputRef.current?.click()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") inputRef.current?.click(); }}
      className="cursor-pointer rounded-sm transition-all p-6 text-center"
      style={{
        border: `2px dashed ${hover ? "var(--gold)" : "rgba(212,175,55,0.35)"}`,
        background: hover ? "rgba(212,175,55,0.06)" : "rgba(0,0,0,0.15)",
      }}
      data-testid={`${testid}-zone`}
    >
      {busy ? (
        <div className="flex flex-col items-center gap-2 py-2" data-testid={`${testid}-busy`}>
          <Loader2 className="h-7 w-7 animate-spin" style={{ color: "var(--gold)" }} />
          <div className="text-sm tavern-heading" style={{ color: "var(--silver)" }}>Uploading… {progress}%</div>
          <div className="w-48 h-1 rounded mt-2" style={{ background: "rgba(255,255,255,0.1)" }}>
            <div className="h-1 rounded transition-all" style={{ width: `${progress}%`, background: "var(--gold)" }} />
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2">
          <Upload className="h-7 w-7" style={{ color: "var(--gold)" }} />
          <div className="tavern-heading text-base gold-text">{label}</div>
          <div className="text-xs" style={{ color: "var(--muted)" }}>
            Drag &amp; drop, or click to browse · JPG, PNG, WEBP · up to 20MB
          </div>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={onPick}
        data-testid={`${testid}-input`}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}
